import { TONE_COLORS, type CreatureTone } from "@eldrun/content";
import { Color, ShaderMaterial } from "three";
import { gameColor } from "../style/roles.ts";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { ATMOSPHERE_UNIFORMS } from "./common.ts";
import { DECAL_SLOTS } from "../decal-batch.ts";

const vertexShader = /* glsl */ `
in float aRadial;
in float aAngular;
// Lote (revisão da Fase 13, \`DecalBatch\`): cada aviso na sua vaga — andamento, lampejo, bordas
// laterais e borda dupla do chefe (x, y, z, w) e o tom de quem ataca.
in float aSlot;
uniform vec4 uSlot[${DECAL_SLOTS}];
uniform vec3 uSlotColor[${DECAL_SLOTS}];
out float vRadial;
out float vAngular;
flat out vec4 vSlot;
flat out vec3 vTone;

void main() {
  vRadial = aRadial;
  vAngular = aAngular;
  int slot = int(aSlot + 0.5);
  vSlot = uSlot[slot];
  vTone = uSlotColor[slot];
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
${ATMOSPHERE_UNIFORMS}
uniform float uSemLampejo;
/** Modo daltônico (F75): hachura em faixas duras e mais fortes (o padrão lê sem a cor). */
uniform float uReforco;
in float vRadial;
in float vAngular;
// Malha neon (F60): o tom de quem ataca (laranja; vermelho no chefe, com borda dupla) e a hachura.
flat in vec4 vSlot;
flat in vec3 vTone;

void main() {
  float uProgress = vSlot.x;
  float uFade = vSlot.y;
  float uArc = vSlot.z;
  float uBossLook = vSlot.w;
  vec3 uTone = vTone;
  // Contorno da área inteira desde o início; o preenchimento cresce do centro até a borda no
  // instante do impacto, com uma frente mais clara e a hachura que corre para fora (doc 06).
  float aa = fwidth(vRadial) * 1.5;
  float edgeR = smoothstep(0.93 - aa, 0.96, vRadial);
  float edgeA = uArc * smoothstep(0.94 - fwidth(vAngular) * 1.5, 0.97, abs(vAngular));
  float edge = max(edgeR, edgeA);
  float filled = 1.0 - smoothstep(uProgress - aa, uProgress, vRadial);
  float front = filled * smoothstep(uProgress - 0.08, uProgress, vRadial);
  float stripes = 0.5 + 0.5 * sin((vRadial * 10.0 + vAngular * 6.0 - uTime * 2.0) * 6.2832);
  float innerEdge = uBossLook * (smoothstep(0.84 - aa, 0.86, vRadial) - smoothstep(0.89, 0.91 + aa, vRadial));
  edge = max(edge, innerEdge);
  stripes = mix(stripes, step(0.5, stripes), uReforco);
  float alpha = 0.16 + (0.08 + 0.14 * uReforco) * stripes;
  alpha = max(alpha, filled * 0.45);
  alpha = max(alpha, edge * 0.85);
  alpha = max(alpha, front * 0.95);
  vec3 base = uTone;
  vec3 hot = mix(uTone, vec3(1.0), 0.65);
  // Depois do impacto: lampejo claro que some em 0,3 s.
  // Com a redução de lampejos (Opções), o aviso só some, sem clarear.
  float flash = uFade > 0.0 && uSemLampejo < 0.5 ? 1.0 - uFade : 0.0;
  vec3 color = mix(base, hot, max(front, flash));
  alpha = uFade > 0.0 ? (uSemLampejo > 0.5 ? alpha : 0.9) * (1.0 - uFade) : alpha;
  gl_FragColor = vec4(color, alpha);
  #include <colorspace_fragment>
}
`;

/** O tom (linear, no modo de cor em uso) e a borda dupla de quem ataca. */
export function telegraphTone(tone: CreatureTone = "inimigo"): { color: Color; boss: number } {
  return { color: new Color(gameColor(TONE_COLORS[tone])), boss: tone === "chefe" ? 1 : 0 };
}

/**
 * Material dos avisos de ataque no chão, em lote (`DecalBatch`): sem escrever profundidade e puxado para
 * a câmera (`polygonOffset`) para não brigar com o terreno logo abaixo.
 */
export function createTelegraphMaterial(atmosphere: AtmosphereUniforms): ShaderMaterial {
  return new ShaderMaterial({
    name: "aviso-de-ataque",
    uniforms: { ...atmosphere, uSemLampejo: { value: 0 }, uReforco: { value: 0 } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -4,
  });
}
